import { Controller } from "@hotwired/stimulus"

// Main onboarding controller — progress bar, validation, interactive elements.
export default class extends Controller {
  static targets = ["progressBar", "stepLabel", "stateGrid", "businessAddress",
                     "colorHexInput",
                     "customRevenue", "customVolume", "customGoal",
                     "leadMixRange", "realtimePct", "agedPct", "serverErrorAlert"]
  static values = { currentStep: Number, totalSteps: Number }

  connect() {
    this.updateProgress()
    this.setupFormValidation()
    this.surfaceServerErrors()
  }

  disconnect() {
    if (this._flashTimer) clearTimeout(this._flashTimer)
  }

  // 422 errors are surfaced inline, next to the form the user was filling in —
  // a toast would detach the reason from the field (the welcome layout does
  // render a #toast-container since campaign #12564 / PE-048; that is not why).
  surfaceServerErrors() {
    if (!this.hasServerErrorAlertTarget) return
    const alert = this.serverErrorAlertTarget
    alert.scrollIntoView({ behavior: 'smooth', block: 'center' })
    alert.classList.add('gl-alert-flash')
    // 1200 must match the gl-alert-flash @keyframes duration in _misc.scss.
    this._flashTimer = setTimeout(() => alert.classList.remove('gl-alert-flash'), 1200)
    // The step failed server-side — offer a support ticket right away (unlike the
    // client-side field errors below, this is a real failure, not form friction).
    // Event name: onboarding:error.
    this.dispatch('error', { detail: { message: this.serverErrorText(alert) } })
  }

  // First listed reason from the server error alert (_errors.html.haml renders
  // one <li> per message), used as the "error the user saw" on a support ticket.
  serverErrorText(alert) {
    return (alert.querySelector('li')?.textContent || alert.textContent || '').trim()
  }

  updateProgress() {
    const isComplete = this.currentStepValue >= (this.totalStepsValue - 1)
    const percent = isComplete ? 100 : Math.round((this.currentStepValue / (this.totalStepsValue - 1)) * 100)
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percent}%`
    if (this.hasStepLabelTarget) {
      const stepSpan = this.stepLabelTarget.querySelector('.gl-onboarding-progress-step')
      if (stepSpan && !isComplete) {
        stepSpan.textContent = `Step ${this.currentStepValue} of ${this.totalStepsValue - 1}`
      } else {
        this.stepLabelTarget.textContent = isComplete ? 'Complete!' : `Step ${this.currentStepValue} of ${this.totalStepsValue - 1}`
      }
    }
  }

  // ── Form validation ───────────────────────────────────────────────
  setupFormValidation() {
    const form = this.element.querySelector('.gl-onboarding-form')
    if (!form) return

    form.addEventListener('submit', (e) => {
      // Skip validation for back/skip buttons
      if (e.submitter?.name === '_skip') return

      this.clearErrors()
      const errors = this.validateStep(form)
      if (errors.length > 0) {
        e.preventDefault()
        this.showErrors(errors)
      }
    })

    // Clear errors on input
    form.addEventListener('input', (e) => this.clearSectionError(e.target))
    form.addEventListener('change', (e) => this.clearSectionError(e.target))
  }

  validateStep(form) {
    const step = this.currentStepValue
    const errors = []

    switch (step) {
      case 1: // Agreements
        this.requireCheckbox(form, 'agree_tos', 'You must agree to all policies to continue', errors)
        break

      case 3: // Credentials
        this.requireField(form, 'npn_id', 'NPN is required', errors)
        this.requireAnyChecked(form, 'licensed_states[]', 'Please select at least one licensed state', errors)
        break

      case 4: // Lead Preferences
        this.requireAnyChecked(form, 'lead_preferences[interested_lead_types][]', 'Please select at least one lead type', errors)
        this.requireAnyChecked(form, 'lead_preferences[preferred_qualities][]', 'Please select at least one lead quality', errors)
        this.requireAnyChecked(form, 'lead_preferences[preferred_languages][]', 'Please select at least one language', errors)
        this.requireAnyChecked(form, 'default_days_per_week[]', 'Please select at least one day', errors)
        break

      case 5: // Business Goals
        this.requireAnyRadioOrCustom(form, 'business_goals[monthly_revenue_target]', 'business_goals[monthly_revenue_target_custom]', 'Please select or enter a revenue goal', errors)
        this.requireAnyRadioOrCustom(form, 'business_goals[weekly_lead_volume_goal]', 'business_goals[weekly_lead_volume_goal_custom]', 'Please select or enter a weekly lead volume', errors)
        this.requireAnyRadioOrCustom(form, 'business_goals[primary_goal]', 'business_goals[primary_goal_custom]', 'Please select your primary goal', errors)
        break

      case 7: // Business Info
        this.requireField(form, 'home_address_street', 'Street address is required', errors)
        this.requireField(form, 'home_address_city', 'City is required', errors)
        this.requireSelect(form, 'home_address_state', 'State is required', errors)
        this.requireField(form, 'home_address_zip', 'Zip code is required', errors)
        this.requireSelect(form, 'time_zone', 'Time zone is required', errors)
        this.requireField(form, 'imo', 'IMO / Agency is required', errors)
        break
    }

    return errors
  }

  requireField(form, name, message, errors) {
    const input = form.querySelector(`[name="${name}"]`)
    if (!input || !input.value.trim()) {
      errors.push({ element: input?.closest('.gl-onboarding-field') || input?.closest('.gl-onboarding-section'), message })
    }
  }

  requireSelect(form, name, message, errors) {
    const select = form.querySelector(`[name="${name}"]`)
    if (!select || !select.value) {
      errors.push({ element: select?.closest('.gl-onboarding-field') || select?.closest('.gl-onboarding-section'), message })
    }
  }

  requireCheckbox(form, name, message, errors) {
    const cb = form.querySelector(`[name="${name}"]`)
    if (!cb || !cb.checked) {
      errors.push({ element: cb?.closest('.gl-onboarding-section') || cb?.closest('.gl-form-check'), message })
    }
  }

  requireAnyChecked(form, name, message, errors) {
    // Use escaped selector for names with brackets. A hidden input under the
    // name counts as checked: the NIPR-locked credentials step submits its
    // read-only state chips that way (#14740).
    const escapedName = CSS.escape(name)
    const checked = form.querySelectorAll(`[name=${escapedName}]:checked, input[type="hidden"][name=${escapedName}]`)
    if (checked.length === 0) {
      const all = form.querySelectorAll(`[name=${escapedName}]`)
      const first = all.length > 0 ? all[0] : null
      const section = first?.closest('.gl-onboarding-section') || first?.closest('.gl-onboarding-field')
      errors.push({ element: section || form.querySelector('.gl-onboarding-section'), message })
    }
  }

  requireAnyRadio(form, name, message, errors) {
    const esc = CSS.escape(name)
    const checked = form.querySelector(`[name=${esc}]:checked`)
    if (!checked) {
      const first = form.querySelector(`[name=${esc}]`)
      errors.push({ element: first?.closest('.gl-onboarding-section') || first?.closest('.gl-onboarding-field'), message })
    }
  }

  requireAnyRadioOrCustom(form, radioName, customName, message, errors) {
    const escRadio = CSS.escape(radioName)
    const escCustom = CSS.escape(customName)
    const checked = form.querySelector(`[name=${escRadio}]:checked`)
    const custom = form.querySelector(`[name=${escCustom}]`)
    const customFilled = Boolean(custom && custom.value.trim())
    // The "Custom" radio (value="custom") only satisfies the requirement once
    // its paired amount is filled — otherwise the literal 'custom' sentinel
    // would pass as a real value (matches OnboardingStepService#resolved_goal).
    const satisfied = checked ? (checked.value !== 'custom' || customFilled) : customFilled
    if (!satisfied) {
      const first = form.querySelector(`[name=${escRadio}]`) || form.querySelector(`[name=${escCustom}]`)
      errors.push({ element: first?.closest('.gl-onboarding-section') || first?.closest('.gl-onboarding-field'), message })
    }
  }

  showErrors(errors) {
    let firstErrorEl = null

    errors.forEach(({ element, message }) => {
      // Fallback to the form itself if element is null
      const target = element || this.element.querySelector('.gl-onboarding-form') || this.element
      target.classList.add('gl-onboarding-error')

      if (!target.querySelector('.gl-onboarding-error-msg')) {
        const msg = document.createElement('div')
        msg.className = 'gl-onboarding-error-msg'
        const icon = document.createElement('i')
        icon.className = 'ph-fill ph-warning-circle'
        msg.appendChild(icon)
        msg.appendChild(document.createTextNode(` ${message}`))
        target.appendChild(msg)
      }

      if (!firstErrorEl) firstErrorEl = target
    })

    if (firstErrorEl) {
      const errorMsg = firstErrorEl.querySelector('.gl-onboarding-error-msg')
      ;(errorMsg || firstErrorEl).scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // Client-side field validation, so this goes out as onboarding:client-error —
    // signup-support only escalates it once the user has hit it repeatedly. A
    // first "you forgot a checkbox" is form friction, not a support case.
    this.dispatch('client-error', { detail: { message: errors[0]?.message } })
  }

  clearErrors() {
    this.element.querySelectorAll('.gl-onboarding-error').forEach(el => el.classList.remove('gl-onboarding-error'))
    this.element.querySelectorAll('.gl-onboarding-error-msg').forEach(el => el.remove())
  }

  clearSectionError(element) {
    const section = element.closest('.gl-onboarding-section') || element.closest('.gl-onboarding-error')
    if (section) {
      section.classList.remove('gl-onboarding-error')
      section.querySelector('.gl-onboarding-error-msg')?.remove()
    }
  }

  // ── Navigation ─────────────────────────────────────────────────────
  goBack() {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/onboarding/go_back'
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'authenticity_token'
      input.value = csrfToken
      form.appendChild(input)
    }
    document.body.appendChild(form)
    // form.submit() fires no submit event, so the shared spinning-goat lifecycle
    // (lib/slow_visit_overlay.js) cannot see this navigation on its own: announce
    // it first, and the goat shows after the same no-flash delay while the POST
    // and its redirect are in flight (#13805). If the browser refuses the submit,
    // withdraw it so nothing stays covered. The transport is unchanged.
    const loader = window.GoatLoadingOverlay
    loader?.beginNavigation?.('onboarding-back')
    try {
      form.submit()
    } catch (error) {
      loader?.settleNavigation?.()
      throw error
    } finally {
      // submit() navigates, but a Turbo snapshot of this page must not carry a
      // stray hidden form into a restored copy.
      form.remove()
    }
  }

  // ── State chip toggles ──────────────────────────────────────────────
  toggleStateChip(event) {
    const label = event.target.closest('.gl-onboarding-state-chip')
    if (label) label.classList.toggle('selected', event.target.checked)
  }

  selectAllStates() {
    this.stateGridTarget.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      // NY excluded due to regulations
      if (cb.value === 'NY') {
        cb.checked = false
        cb.closest('.gl-onboarding-state-chip')?.classList.remove('selected')
      } else {
        cb.checked = true
        cb.closest('.gl-onboarding-state-chip')?.classList.add('selected')
      }
    })
    this.notifyStateGridChanged()
    this.clearSectionError(this.stateGridTarget)
  }

  clearAllStates() {
    this.stateGridTarget.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.checked = false
      cb.closest('.gl-onboarding-state-chip')?.classList.remove('selected')
    })
    this.notifyStateGridChanged()
  }

  // Fire ONE bubbling change event from the grid after a bulk select/clear.
  // credentials-gate listens with a single delegated change handler on its
  // root element and recounts checked boxes via querySelectorAll, so one
  // event is sufficient — dispatching per-checkbox (50+ events) would run
  // that recount 50 times for no benefit. Direct .checked assignment alone
  // skips the native event, so the explicit dispatch is still required.
  notifyStateGridChanged() {
    this.stateGridTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // ── Lead mix slider ────────────────────────────────────────────────
  updateLeadMix(event) {
    const realtime = parseInt(event.target.value, 10)
    const aged = 100 - realtime

    if (this.hasRealtimePctTarget) {
      this.realtimePctTarget.querySelector('.gl-lead-mix-pct').textContent = `${realtime}%`
    }
    if (this.hasAgedPctTarget) {
      this.agedPctTarget.querySelector('.gl-lead-mix-pct').textContent = `${aged}%`
    }

    // Update the tip text
    const tip = this.element.querySelector('.gl-lead-mix-tip span')
    if (tip) {
      if (realtime >= 80) {
        tip.textContent = "You're prioritizing speed — great for agents who close fast on fresh intent."
      } else if (realtime >= 50) {
        tip.textContent = "A balanced mix gives you both quality and volume. Most agents start here."
      } else {
        tip.textContent = "Heavy on aged leads — ideal for high-volume prospecting at lower cost."
      }
    }

    // Update CSS custom property for the track fill
    const track = event.target.closest('.gl-lead-mix-track')
    if (track) {
      track.style.setProperty('--mix-pct', `${realtime}%`)
    }
  }

  // ── Custom field show/hide for goal cards ──────────────────────────
  showCustomField(event) {
    const section = event.currentTarget.closest('.gl-onboarding-section')
    if (!section) return
    const customInput = section.querySelector('.gl-onboarding-custom-input')
    if (customInput) {
      customInput.classList.remove('d-none')
      const input = customInput.querySelector('input')
      if (input) input.focus()
    }
  }

  hideCustomField(event) {
    const section = event.target.closest('.gl-onboarding-section')
    if (!section) return
    const customInput = section.querySelector('.gl-onboarding-custom-input')
    if (customInput) {
      customInput.classList.add('d-none')
      const input = customInput.querySelector('input')
      if (input) input.value = ''
    }
  }

  // ── Number formatting ────────────────────────────────────────────
  formatCurrency(event) {
    const input = event.target
    const raw = input.value.replace(/[^0-9]/g, '')
    if (raw === '') { input.value = ''; return }
    const num = parseInt(raw, 10)
    input.value = '$' + num.toLocaleString()
    // Store raw number in a hidden sibling or data attribute for form submission
    const hidden = input.nextElementSibling
    if (hidden?.type === 'hidden') hidden.value = raw
  }

  formatInteger(event) {
    const input = event.target
    const raw = input.value.replace(/[^0-9]/g, '')
    if (raw === '') { input.value = ''; return }
    const num = parseInt(raw, 10)
    input.value = num.toLocaleString()
    const hidden = input.nextElementSibling
    if (hidden?.type === 'hidden') hidden.value = raw
  }

  // ── Type card toggles ──────────────────────────────────────────────
  toggleTypeCard(event) {
    const card = event.target.closest('.gl-onboarding-type-card')
    if (card) card.classList.toggle('selected', event.target.checked)
  }

  // ── Pill toggles ──────────────────────────────────────────────────
  togglePill(event) {
    const pill = event.target.closest('.gl-onboarding-pill')
    if (!pill) return
    if (event.target.type === 'radio') {
      const group = pill.closest('.gl-onboarding-pill-group')
      group?.querySelectorAll('.gl-onboarding-pill').forEach(p => p.classList.remove('selected'))
      pill.classList.add('selected')
    } else {
      pill.classList.toggle('selected', event.target.checked)
    }
  }

  // ── Goal card toggles ─────────────────────────────────────────────
  toggleGoalCard(event) {
    const card = event.target.closest('.gl-onboarding-goal-card')
    if (!card) return
    const grid = card.closest('.gl-onboarding-goal-grid')
    grid?.querySelectorAll('.gl-onboarding-goal-card').forEach(c => c.classList.remove('selected'))
    card.classList.add('selected')
  }

  // ── Color picker ──────────────────────────────────────────────────
  selectColor(event) {
    const swatch = event.target.closest('.gl-onboarding-color-swatch')
    if (!swatch) return
    this.element.querySelectorAll('.gl-onboarding-color-swatch').forEach(s => s.classList.remove('selected'))
    swatch.classList.add('selected')
    // Sync hex input
    const color = event.target.value
    if (this.hasColorHexInputTarget) this.colorHexInputTarget.value = color
    const colorPicker = this.element.querySelector('[name="accent_color_custom"]')
    if (colorPicker) colorPicker.value = color
  }

  customColor(event) {
    const color = event.target.value
    // Deselect all swatches
    this.element.querySelectorAll('.gl-onboarding-color-swatch').forEach(s => s.classList.remove('selected'))
    // Update hex input
    if (this.hasColorHexInputTarget) this.colorHexInputTarget.value = color
    // Update hidden radio — create a temp one if needed
    this.setAccentColorValue(color)
  }

  customColorHex(event) {
    let color = event.target.value.trim()
    if (!color.startsWith('#')) color = '#' + color
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      this.element.querySelectorAll('.gl-onboarding-color-swatch').forEach(s => s.classList.remove('selected'))
      const colorPicker = this.element.querySelector('[name="accent_color_custom"]')
      if (colorPicker) colorPicker.value = color
      this.setAccentColorValue(color)
    }
  }

  setAccentColorValue(color) {
    // Check if any swatch matches, if so select it
    const matching = this.element.querySelector(`.gl-onboarding-color-swatch input[value="${color}"]`)
    if (matching) {
      matching.checked = true
      matching.closest('.gl-onboarding-color-swatch')?.classList.add('selected')
    } else {
      // Uncheck all swatches and use custom value
      this.element.querySelectorAll('.gl-onboarding-color-swatch input').forEach(r => { r.checked = false })
    }
  }

  // ── Business address toggle ───────────────────────────────────────
  toggleSameAddress(event) {
    const pill = event.target.closest('.gl-onboarding-pill')
    if (pill) pill.classList.toggle('selected', event.target.checked)
    if (!this.hasBusinessAddressTarget) return

    const checked = event.target.checked
    const biz = this.businessAddressTarget
    const fields = biz.querySelectorAll('input.gl-onboarding-input, select.gl-onboarding-input')

    if (checked) {
      // Copy home address values into business fields
      const homeStreet = document.querySelector('[name="home_address_street"]')
      const homeCity = document.querySelector('[name="home_address_city"]')
      const homeState = document.querySelector('[name="home_address_state"]')
      const homeZip = document.querySelector('[name="home_address_zip"]')
      const homeCountry = document.querySelector('[name="home_address_country"]')

      const bizStreet = biz.querySelector('[name="business_address_street"]')
      const bizCity = biz.querySelector('[name="business_address_city"]')
      const bizState = biz.querySelector('[name="business_address_state"]')
      const bizZip = biz.querySelector('[name="business_address_zip"]')
      const bizCountry = biz.querySelector('[name="business_address_country"]')

      if (bizStreet && homeStreet) bizStreet.value = homeStreet.value
      if (bizCity && homeCity) bizCity.value = homeCity.value
      if (bizState && homeState) bizState.value = homeState.value
      if (bizZip && homeZip) bizZip.value = homeZip.value
      if (bizCountry && homeCountry) bizCountry.value = homeCountry.value

      // Disable all business fields
      fields.forEach(f => { f.disabled = true; f.classList.add('gl-input-disabled') })
    } else {
      // Re-enable fields
      fields.forEach(f => { f.disabled = false; f.classList.remove('gl-input-disabled') })
    }

    // Always show the section (never hide it)
    biz.classList.remove('d-none')
  }
}
